import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'

import {
  getBoardCurrency,
  getIsFetching,
  getRoomsByAvailability,
} from 'src/effects/Availability/selectors'

import { clickAvailabilityRoom } from 'client/contexts/Tracking/events'
import { fetchAvailabilityRequestAction } from 'src/effects/Availability/actions'
import { getIsFetching as getIsFetchingProperties } from 'client/contexts/Property/selectors'
import { getIsUserDataReady, getLoggedIn } from 'client/contexts/Login/selectors'
import { getSearch, getSearchPaxes } from 'src/effects/Search/selectors'
import { useMarket } from 'src/hooks/useMarket'
import { RoomsProvider } from './context'
import { routes } from 'src/constants/routes'
import { scrollToPosition } from 'lib/utils/scroll'
import { sendTrackingRequestAction } from 'client/contexts/Tracking/actions'
import { setSearchParamsLocalStorage } from 'lib/storage'
import isValidArray from 'lib/functions/isValidArray'

import ErrorBlock from 'client/components/ErrorBlock'
import RoomsAvailability from './Availability/RoomsAvailability'
import RoomsSelector from 'client/components/RoomsSelector'
import { useBusiness } from 'src/hooks/useBusiness'
import { useDevice } from 'src/hooks/useDevice'

import styles from './styles.module.css'

import { DEFAULT_AFFILIATE_ID } from 'src/pages/_middleware/defaultAffiliate'
import { setPurchaseFromLocalStorageAction } from 'client/contexts/Purchase/actions'

import { usePrices } from 'src/hooks/usePrices'
import { PriceType } from 'src/types/prices'
import useTabActive from 'src/hooks/useTabActive'
import { hasElapsedTime } from './utils'
import { DEFAULT_SOURCE } from 'src/constants/sources'
import type { PropertyAPIResponse as PropertyType } from 'src/types/property'
import type { BoardType } from 'src/schemas/availability'
import { Devices } from 'src/types/device'

import RoomCardPlaceholder from './RoomResponsive/RoomCardPlaceholder'
import Pill from 'src/comps/Atom/Pill'
import CircularProgress from 'src/comps/Atom/CircularProgress'
import { isValidSource } from 'src/utils/affiliate'
import { updateQueryStringFilters } from 'client/pages/Serp/utils'
import { hasBreakfast, isAllInclusive } from './RoomResponsive/OfferResponsive/Board'
import { Text } from 'src/comps/Atom/Text'
import { useS4LContext } from '@s4l/react-context'
import BoardFilter from './RoomResponsive/BoardFilter'
import Calendar from 'client/icons/Calendar'
import CreditCard from 'client/icons/CreditCard'
import BedBreakfast from 'client/icons/boards/BedBreakfast'
import AllInclusive from 'client/icons/boards/AllInclusive'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { PAGES, SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { NoRooms } from '@s4l/uikit'

const MILIS_TO_REQUEST_NEW_AV = 20 * 60000 // 20 min
const AVAILABILITY_MIN_ROOMS_TO_SHOW_FILTERS = 3

export interface IRooms {
  property: PropertyType
  source: string
}

export interface IBoard extends Omit<BoardType, 'idAvailability'> {
  paxesId: string
  insuranceSelected: boolean
  availabilityId: string
  finalPrice: number
  totalWithFees: number
}

interface IHandleSelectRoomParams {
  board: IBoard
  isRemove: boolean
  closeGroup: () => void
}

const Rooms = ({ property, source }: IRooms) => {
  const router = useRouter()
  const { query } = router
  const { total = '' } = query
  const { i18n } = useS4LContext()

  const formRef = useRef<HTMLFormElement>(null)
  const hasShownNoRoomsRef = useRef<boolean>(false)
  const { language, market } = useMarket()
  const { adults, children, checkIn, checkOut } = useSelector(getSearch)
  const { slug, country, citySlug } = property

  const searchParams = useMemo(
    () => ({
      adults,
      children,
      checkIn,
      checkOut,
    }),
    [adults, children, checkIn, checkOut]
  )

  const isFetchingProperty = useSelector(getIsFetchingProperties)
  const isFetchingRooms = useSelector(getIsFetching)
  const isLogged = useSelector(getLoggedIn)
  const isUserReady = useSelector(getIsUserDataReady)

  const { isBot } = useDevice()
  const { affiliate, mobileRate } = useBusiness()

  const availability = useSelector(getRoomsByAvailability)
  const capacity = useSelector(getSearchPaxes)
  const currency = useSelector(getBoardCurrency)

  const dispatch = useDispatch()
  const [selectedRooms, setSelectedRooms] = useState<IBoard[]>([])
  const [isBookingRoom, setIsBookingRoom] = useState(false)
  const [isFirstAvailabilityRequest, setIsFirstAvailabilityRequest] = useState(true)
  const [lastFetchTime, setLastFetchTime] = useState<number | undefined>(undefined)

  const [filters, setFilters] = useState(() => {
    const { filters: filtersParam } = query as { filters: string }

    const currentFilters = filtersParam?.split(',').filter(Boolean) || []

    return {
      breakfast: currentFilters.includes('breakfast'),
      allInclusive: currentFilters.includes('allInclusive'),
      payLater: currentFilters.includes('payLater'),
      freeCancellation: currentFilters.includes('freeCancellation'),
    }
  })

  const isLoading = isFetchingRooms || isFetchingProperty
  const isMultiRoom = availability.length > 1
  const noResults = !isValidArray(availability)
  const { priceType } = usePrices({ rooms: [] })

  const visibilityState = useTabActive()
  const { visibility } = visibilityState

  const { sendEvent: sendBoardFilter } = useKafkaEvent({
    eventName: 'board_filter_change',
    page: PAGES.DETAIL,
  })

  const { sendEvent: showNoRooms } = useKafkaEvent({
    eventName: 'no_rooms',
    eventType: 'impression',
    page: PAGES.DETAIL,
  })

  const { sendEvent: clearRoomsFilter } = useKafkaEvent({
    eventName: 'no_rooms',
    eventType: 'click',
    page: PAGES.DETAIL,
  })

  const resetFilters = useCallback(() => {
    setFilters({
      breakfast: false,
      allInclusive: false,
      payLater: false,
      freeCancellation: false,
    })

    clearRoomsFilter({ section: SECTIONS.NO_ROOMS })
  }, [clearRoomsFilter])

  const fetchAvailability = useCallback(() => {
    const sendSource = isFirstAvailabilityRequest && !!query.source
    const sendMinSellingRate = isFirstAvailabilityRequest && !!query.total
    const membership = query?.membership

    // If the tab is not active, we don't want to make the request
    // because the availability expires in 20-30 minutes and this
    // delay the availability request until the last moment
    // With this we try to reduce the purchase_av_not_found errors

    if (!visibility) return

    dispatch(
      fetchAvailabilityRequestAction({
        ...searchParams,
        id: property.id,
        isLogged,
        isBot,
        language,
        market,
        currency,
        affiliate: affiliate ?? DEFAULT_AFFILIATE_ID,
        source: sendSource && isValidSource(source) ? source : DEFAULT_SOURCE,
        ...(sendMinSellingRate && { minSellingRate: parseFloat(total as string) }),
        queryParams: {
          date_feed: query?.date_feed,
        },
        ...(mobileRate && {
          device: Devices.Mobile,
        }),
        ...(membership && { membership: `${membership}`.toLowerCase() === 'true' }),
      })
    )

    setSearchParamsLocalStorage(searchParams)
    setIsFirstAvailabilityRequest(false)
    setIsBookingRoom(false)
    setSelectedRooms([])
    setLastFetchTime(new Date().getTime())
  }, [
    isFirstAvailabilityRequest,
    query.source,
    query.total,
    query?.date_feed,
    query?.membership,
    visibility,
    searchParams,
    property.id,
    isLogged,
    isBot,
    language,
    market,
    currency,
    affiliate,
    source,
    total,
    mobileRate,
    dispatch,
  ])

  // Update the URL with query parameters
  useEffect(() => {
    const updatedQueryString = updateQueryStringFilters(searchParams)
    const currentFilters = Object.entries(filters)
      .filter(([, value]) => value)
      .map(([key]) => key)
      .join(',')

    let finalQuery = updatedQueryString
    if (currentFilters) {
      finalQuery = finalQuery
        ? `${finalQuery}&filters=${currentFilters}`
        : `filters=${currentFilters}`
    }

    if (window.location.search !== `?${finalQuery}`) {
      router.replace(
        {
          pathname: window.location.pathname,
          query: finalQuery,
        },
        undefined,
        { shallow: false, scroll: false }
      )
    }
  }, [searchParams, router, filters])

  useEffect(() => {
    fetchAvailability()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adults,
    capacity.adults,
    capacity.children,
    checkIn,
    checkOut,
    children,
    isLogged,
    language,
    market,
    affiliate,
    property.id,
    isUserReady,
    mobileRate,
    query?.date_feed,
    query?.aid,
    query?.membership,
  ])

  useEffect(() => {
    if (!visibility) return

    const shouldFetch = !lastFetchTime || hasElapsedTime(lastFetchTime, MILIS_TO_REQUEST_NEW_AV)

    if (shouldFetch) {
      fetchAvailability()
    }
  }, [visibility, lastFetchTime, fetchAvailability])

  const filteredAvailability = availability
    .map(rooms => {
      return rooms.filter((room: { paymentPolicy: string; payRate: number; code: string }) => {
        return !(
          (filters.payLater && room.payRate > 0) ||
          (filters.breakfast && !hasBreakfast(room.code)) ||
          (filters.allInclusive && !isAllInclusive(room.code)) ||
          (filters.freeCancellation && room.paymentPolicy !== 'NOR' && room.paymentPolicy !== 'SFL')
        )
      })
    })
    .filter(rooms => rooms.length > 0)

  const showFilters = {
    breakfast: filteredAvailability.some(rooms =>
      rooms.some((room: { code: string }) => hasBreakfast(room.code))
    ),
    allInclusive: filteredAvailability.some(rooms =>
      rooms.some((room: { code: string }) => isAllInclusive(room.code))
    ),
    payLater: filteredAvailability.some(rooms =>
      rooms.some((room: { payRate: number }) => room.payRate === 0)
    ),
    freeCancellation: filteredAvailability.some(rooms =>
      rooms.some(
        (room: { paymentPolicy: string }) =>
          room.paymentPolicy === 'NOR' || room.paymentPolicy === 'SFL'
      )
    ),
  }

  const hasFilters =
    availability.some(room => room.length >= AVAILABILITY_MIN_ROOMS_TO_SHOW_FILTERS) &&
    Object.values(showFilters).some(Boolean)

  useEffect(() => {
    if (
      filteredAvailability.length === 0 &&
      availability.length > 0 &&
      !hasShownNoRoomsRef.current &&
      !isLoading &&
      Object.values(filters).some(Boolean)
    ) {
      showNoRooms(
        { section: SECTIONS.NO_ROOMS },
        {
          current_filters: {
            breakfast: filters.breakfast,
            all_inclusive: filters.allInclusive,
            pay_later: filters.payLater,
            free_cancellation: filters.freeCancellation,
          },
        }
      )
      hasShownNoRoomsRef.current = true
    }
  }, [filteredAvailability, availability, filters, showNoRooms, isLoading])

  const handleSubmit = useCallback(
    async (boards: IBoard[]) => {
      const availabilityIds = boards.map(board => board.availabilityId).join()
      const insuranceIds = boards
        .filter(board => board.insuranceSelected)
        .map(board => board.availabilityId)
        .join()

      dispatch(setPurchaseFromLocalStorageAction())

      router.push({
        pathname: routes.checkout.pathname,
        query: { availabilityIds, insuranceIds, city: citySlug, country, slug },
      })
    },
    [citySlug, country, slug, dispatch, router]
  )

  const sendClickEvent = useCallback(
    (board: IBoard) => {
      const availabilityCount = availability[selectedRooms.length]?.length || 0

      dispatch(
        sendTrackingRequestAction({
          event: clickAvailabilityRoom({ board, availabilityCount, propertyId: property.id }),
        })
      )
    },
    [availability, selectedRooms.length, property.id, dispatch]
  )

  const handleSelectRoom = useCallback(
    ({ board, isRemove, closeGroup }: IHandleSelectRoomParams) => {
      let selection = selectedRooms

      if (isRemove) {
        selection = selection.filter(room => room.availabilityId !== board.availabilityId)
        setSelectedRooms(selection)
        return
      }

      const availabilityBoard = selection.find(room => room.paxesId === board.paxesId)
      if (availabilityBoard) {
        selection = selection.filter(
          room => room.availabilityId !== availabilityBoard.availabilityId
        )
      }

      const selectionUpdated = [...selection, board]
      setSelectedRooms(selectionUpdated)
      sendClickEvent(board)

      if (selection.length === availability.length - 1) {
        setIsBookingRoom(true)
        handleSubmit(selectionUpdated)
        return
      }

      closeGroup()
      scrollToPosition({ ref: formRef, margin: -120 })
    },
    [selectedRooms, availability.length, sendClickEvent, handleSubmit]
  )

  const handleFiltersChange = useCallback(
    (event: React.ChangeEvent<HTMLFormElement>) => {
      const { value, checked } = event.target

      setFilters(prev => {
        return { ...prev, [value]: checked }
      })

      const section = {
        breakfast: SECTIONS.BOARD_FILTERS_BREAKFAST,
        allInclusive: SECTIONS.BOARD_FILTERS_ALL_INCLUSIVE,
        payLater: SECTIONS.BOARD_FILTERS_PAY_LATER,
        freeCancellation: SECTIONS.BOARD_FILTERS_FREE_CANCELLATION,
      } as const

      sendBoardFilter(
        { section: section[value as keyof typeof section] },
        {
          action: checked ? 'activate' : 'deactivate',
          filters: {
            breakfast: filters.breakfast,
            all_inclusive: filters.allInclusive,
            pay_later: filters.payLater,
            free_cancellation: filters.freeCancellation,
            [value.replace(/([A-Z])/g, '_$1').toLowerCase()]: checked,
          },
        }
      )
      hasShownNoRoomsRef.current = false
    },
    [filters, sendBoardFilter]
  )

  if (isLoading) {
    return (
      <div className={cn(styles.rooms, styles.loadingContainer)}>
        <RoomCardPlaceholder />
        <Pill
          className={styles.pill}
          leftIcon={<CircularProgress size="s" />}
          label={i18n.formatMessage({ id: 'offers.loading' })}
        />
      </div>
    )
  }

  if (noResults) {
    return (
      <div className={styles.rooms}>
        <ErrorBlock errorId="noAvailability" />
      </div>
    )
  }

  const providerState = {
    handleSelectRoom,
    isBookingRoom,
    isMultiRoom,
    selectedRooms,
    setIsBookingRoom,
  }

  return (
    <RoomsProvider value={providerState}>
      <div className={styles.rooms}>
        {hasFilters && (
          <div className={styles.filtersContainer}>
            <Text variant="textBodyL" weight="weight3">
              {i18n.formatMessage({ id: 'board-filter.filter-by' })}
            </Text>
            <form className={styles.filtersForm} onChange={handleFiltersChange}>
              {showFilters.breakfast && (
                <BoardFilter
                  value="breakfast"
                  icon={<BedBreakfast size={16} />}
                  checked={filters.breakfast}
                >
                  {i18n.formatMessage({ id: 'global.breakfast' })}
                </BoardFilter>
              )}
              {showFilters.allInclusive && (
                <BoardFilter
                  value="allInclusive"
                  icon={<AllInclusive size={16} />}
                  checked={filters.allInclusive}
                >
                  {i18n.formatMessage({ id: 'global.all-inclusive' })}
                </BoardFilter>
              )}
              {showFilters.payLater && (
                <BoardFilter
                  value="payLater"
                  icon={<CreditCard size={16} />}
                  checked={filters.payLater}
                >
                  {i18n.formatMessage({ id: 'board-filter.pay-later' })}
                </BoardFilter>
              )}
              {showFilters.freeCancellation && (
                <BoardFilter
                  value="freeCancellation"
                  icon={<Calendar size={16} />}
                  checked={filters.freeCancellation}
                >
                  {i18n.formatMessage({ id: 'board-filter.free-cancellation' })}
                </BoardFilter>
              )}
            </form>
          </div>
        )}

        <form action="/book" data-testid="rooms-form" method="post" ref={formRef}>
          <input type="hidden" name="clickin" value="" />
          <input type="hidden" name="additional_products[insurance]" value="" />
          <input type="hidden" name="language" value={language} />

          {filteredAvailability.length === 0 && <NoRooms onClickCTA={resetFilters} />}

          {filteredAvailability.map((rooms, index) => (
            <Fragment key={rooms[index]?.paxesId}>
              <input type="hidden" name={`id_availabilities[${index}]`} value="" />
              <RoomsAvailability rooms={rooms} showHeader={isMultiRoom} />
            </Fragment>
          ))}
        </form>

        {isMultiRoom && selectedRooms.length > 0 && (
          <RoomsSelector
            roomsSelected={selectedRooms}
            currency={currency}
            showNightNetPrice={priceType === PriceType.Net}
          />
        )}
      </div>
    </RoomsProvider>
  )
}

export default Rooms
